import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["list", "fileInput", "addButton", "addButtonContainer", "counter", "maxMessage", "template"]
  static values = {
    max: Number,
    uploadUrl: String
  }

  connect() {
    this.updateUi()
    this.form = this.element.closest("form")
    if (this.form) {
      this.onSubmit = (event) => this.guardSubmit(event)
      this.form.addEventListener("submit", this.onSubmit)
    }
  }

  disconnect() {
    if (this.form && this.onSubmit) {
      this.form.removeEventListener("submit", this.onSubmit)
    }
  }

  openFilePicker(event) {
    event.preventDefault()
    if (this.totalCount >= this.maxValue || this.uploadingCount > 0) return
    this.fileInputTarget.click()
  }

  guardSubmit(event) {
    if (this.uploadingCount > 0) {
      event.preventDefault()
      event.stopPropagation()
      alert("Please wait for your files to finish uploading before submitting.")
      return
    }

    if (this.hasFileInputTarget && this.fileInputTarget.files.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      alert("Please wait for your file to finish uploading before submitting.")
    }
  }

  async onFileSelected() {
    const input = this.fileInputTarget
    const file = input.files[0]
    if (!file) return

    input.value = ""

    if (this.totalCount >= this.maxValue) return

    const uploadUrl = this.uploadUrlValue || input.dataset.directUploadUrl
    if (!uploadUrl) {
      console.error("[CommentAttachments] Missing direct upload URL")
      return
    }

    const chip = this.appendPendingChip(file.name)
    this.updateUi()

    try {
      const signedId = await this.uploadFile(file, uploadUrl)
      this.completeChip(chip, signedId, file.name)
    } catch (error) {
      console.error("[CommentAttachments] Upload failed:", error)
      chip.remove()
      alert(`Upload failed: ${error.message || error}`)
    } finally {
      this.updateUi()
    }
  }

  remove(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest("[data-comment-attachments-row]")
    if (chip) chip.remove()
    this.updateUi()
  }

  uploadFile(file, url) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url, this)
      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }

  // DirectUpload delegate — fix localhost URLs in development
  directUploadWillCreateBlobWithXHR(xhr) {
    if (window.location.hostname === "localhost" && window.location.port === "4000") {
      const originalOpen = xhr.open
      xhr.open = function(method, requestUrl, ...args) {
        if (requestUrl && requestUrl.includes("https://localhost/rails/active_storage/")) {
          requestUrl = requestUrl.replace("https://localhost/", "http://localhost:4000/")
        }
        return originalOpen.call(this, method, requestUrl, ...args)
      }
    }
  }

  appendPendingChip(filename) {
    const fragment = this.templateTarget.content.cloneNode(true)
    const chip = fragment.querySelector("[data-comment-attachments-row]")
    const nameEl = chip.querySelector("[data-filename]")
    nameEl.textContent = filename
    nameEl.title = filename
    chip.dataset.uploading = "true"
    chip.querySelector("[data-uploading-label]")?.classList.remove("hidden")
    chip.querySelector("[data-remove-button]")?.classList.add("hidden")
    this.listTarget.appendChild(chip)
    return chip
  }

  completeChip(chip, signedId, filename) {
    delete chip.dataset.uploading
    chip.querySelector("[data-uploading-label]")?.classList.add("hidden")
    const removeButton = chip.querySelector("[data-remove-button]")
    removeButton?.classList.remove("hidden")
    removeButton?.setAttribute("aria-label", `Remove ${filename}`)
    chip.querySelector('input[name="comment[file_signed_ids][]"]').value = signedId
  }

  get currentCount() {
    return this.listTarget.querySelectorAll(
      "[data-comment-attachments-row]:not([data-uploading])"
    ).length
  }

  get uploadingCount() {
    return this.listTarget.querySelectorAll("[data-comment-attachments-row][data-uploading]").length
  }

  get totalCount() {
    return this.listTarget.querySelectorAll("[data-comment-attachments-row]").length
  }

  updateUi() {
    const count = this.totalCount
    const atMax = count >= this.maxValue
    const busy = this.uploadingCount > 0

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} of ${this.maxValue} added`
    }

    if (this.hasAddButtonContainerTarget) {
      this.addButtonContainerTarget.classList.toggle("hidden", atMax)
    }

    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = atMax || busy
    }

    if (this.hasMaxMessageTarget) {
      this.maxMessageTarget.classList.toggle("hidden", !atMax)
      if (atMax) {
        this.maxMessageTarget.textContent = `You've added the maximum of ${this.maxValue} files.`
      }
    }

    if (this.hasFileInputTarget) {
      this.fileInputTarget.disabled = atMax || busy
    }
  }
}
